import { useId, useRef, useState, type ReactNode } from 'react'
import { Dialog } from './Dialog'
import { useStaffText } from './StaffText'

/**
 * Dialog with Drawer's contract: a form that submits on Enter, Cancel and the
 * primary action in the footer, and a discard question once something was typed.
 */
export function FormDialog({
  title,
  sub,
  tone = 'dark',
  onClose,
  onSubmit,
  submitLabel,
  valid = true,
  busy = false,
  extra,
  closeLabel,
  hideSubmit = false,
  wide = false,
  children,
}: {
  title: string
  sub?: string
  tone?: 'light' | 'dark'
  onClose: () => void
  onSubmit?: () => void
  submitLabel?: string
  valid?: boolean
  busy?: boolean
  /** Footer's left side: a secondary or destructive action. */
  extra?: ReactNode
  closeLabel?: string
  hideSubmit?: boolean
  wide?: boolean
  children: ReactNode
}) {
  const { say } = useStaffText()
  const formId = useId()
  const dirty = useRef(false)
  const [asking, setAsking] = useState(false)
  const submit = !hideSubmit && onSubmit && submitLabel

  return (
    <Dialog
      title={title}
      sub={sub}
      tone={tone}
      wide={wide}
      onClose={() => (dirty.current ? setAsking(true) : onClose())}
      footNote={asking ? <strong className="form-dialog__ask">{say('ui.discardAsk')}</strong> : extra}
      footer={
        asking ? (
          <>
            <button type="button" className="btn btn--quiet" autoFocus onClick={() => setAsking(false)}>
              {say('ui.keepEditing')}
            </button>
            <button type="button" className="btn btn--danger" onClick={onClose}>
              {say('ui.discard')}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn" onClick={onClose}>
              {closeLabel ?? (submit ? say('common.cancel') : say('ui.close'))}
            </button>
            {submit && (
              <button type="submit" form={formId} className="btn btn--primary" disabled={!valid || busy}>
                {submitLabel}
              </button>
            )}
          </>
        )
      }
    >
      <form
        id={formId}
        className="form-dialog"
        onSubmit={(e) => {
          e.preventDefault()
          if (valid && !busy && onSubmit) onSubmit()
        }}
        onInput={() => {
          dirty.current = true
        }}
        onChange={() => {
          dirty.current = true
        }}
      >
        {children}
      </form>
    </Dialog>
  )
}
